import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  Cog,
  Clock,
  Calendar,
  CheckCircle2,
  Loader2,
  MapPin,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookService = () => {
  const navigate = useNavigate();
  const [installations, setInstallations] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedInstallation, setSelectedInstallation] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [expectedReturnDate, setExpectedReturnDate] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const fetchData = async () => {
    try {
      const [installationsRes, servicesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/my/installations`, { withCredentials: true }),
        axios.get(`${API}/my/services`, { withCredentials: true }),
        axios.get(`${API}/my/bookings`, { withCredentials: true }),
      ]);
      setInstallations(installationsRes.data);
      setServices(servicesRes.data);
      setBookings(bookingsRes.data);
      
      // Pre-select first installation if only one
      if (installationsRes.data.length === 1) {
        setSelectedInstallation(installationsRes.data[0].installation_id);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load your installations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(
        `${API}/service-bookings/available-slots?date=${date}`,
        { withCredentials: true }
      );
      setAvailableSlots(response.data);
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast.error("Failed to load available time slots");
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (day) => {
    if (!day) return;
    const dateStr = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    
    // Don't allow past dates
    const selected = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selected < today) {
      toast.error("Cannot book dates in the past");
      return;
    }
    
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    fetchAvailableSlots(dateStr);
  };

  const handleSubmit = async () => {
    if (!selectedInstallation || !selectedDate || !selectedSlot) {
      toast.error("Please select an installation, date, and time slot");
      return;
    }

    setSubmitting(true);
    try {
      const selectedSvc = services.find((s) => s.service_id === selectedService);
      await axios.post(
        `${API}/service-bookings`,
        {
          installation_id: selectedInstallation,
          service_id: selectedService || null,
          booking_date: selectedDate,
          time_slot: selectedSlot,
          notes: notes || null,
          expected_return_date: selectedSvc?.creates_rental ? (expectedReturnDate || null) : null,
        },
        { withCredentials: true }
      );
      
      toast.success(
        selectedSvc?.creates_rental
          ? "Rental request submitted! A job card will be prepared."
          : "Service booking request submitted!"
      );
      setDialogOpen(false);
      navigate("/customer-dashboard");
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const navigateMonth = (delta) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + delta);
      return newDate;
    });
    setSelectedDate(null);
    setSelectedSlot(null);
    setAvailableSlots([]);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getBookingsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return bookings.filter(
      (b) => b.booking_date === dateStr && (b.status === "pending" || b.status === "confirmed")
    );
  };

  const isPastDay = (day) => {
    if (!day) return false;
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isSelectedDay = (day) => {
    if (!day || !selectedDate) return false;
    const dateStr = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return dateStr === selectedDate;
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const selectedInstallationData = installations.find(
    (i) => i.installation_id === selectedInstallation
  );

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-96 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="book-service-page">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate("/customer-dashboard")}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
          Book a Service
        </h1>
        <p className="text-slate-500 mt-1">
          Select an installation, date, and time slot for your service appointment
        </p>
      </div>

      {installations.length === 0 ? (
        <Card className="rounded-sm border-slate-200">
          <CardContent className="py-12 text-center">
            <Cog className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              No Installations Found
            </h3>
            <p className="text-slate-500">
              You don&apos;t have any installations yet. Please contact us to add your
              installations before booking a service.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Installation Selection */}
          <div className="lg:col-span-1 space-y-6">
            {/* Installation Selector */}
            <Card className="rounded-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">1. Select Installation</CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedInstallation}
                  onValueChange={setSelectedInstallation}
                >
                  <SelectTrigger className="rounded-sm" data-testid="installation-select">
                    <SelectValue placeholder="Choose installation" />
                  </SelectTrigger>
                  <SelectContent>
                    {installations.map((inst) => (
                      <SelectItem
                        key={inst.installation_id}
                        value={inst.installation_id}
                      >
                        {inst.system_name}
                        {inst.location_name ? ` - ${inst.location_name}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedInstallationData && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-sm flex items-center justify-center">
                        <Cog className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium">{selectedInstallationData.system_name}</p>
                        {selectedInstallationData.location_name && (
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <MapPin className="h-3 w-3" />
                            {selectedInstallationData.location_name}
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedInstallationData.next_service_due && (
                      <p className="text-sm text-slate-600">
                        <span className="text-slate-500">Next service due:</span>{" "}
                        <span className="font-medium">
                          {selectedInstallationData.next_service_due}
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Service Selector */}
            {services.length > 0 && (
              <Card className="rounded-sm border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">2. Select Service (Optional)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select
                    value={selectedService}
                    onValueChange={setSelectedService}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="service-select">
                      <SelectValue placeholder="Choose service type (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((svc) => (
                        <SelectItem
                          key={svc.service_id}
                          value={svc.service_id}
                        >
                          {svc.name}
                          {svc.duration_minutes && (
                            <span className="text-slate-500 ml-2">
                              ({Math.floor(svc.duration_minutes / 60)}h {svc.duration_minutes % 60 > 0 ? `${svc.duration_minutes % 60}m` : ''})
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedService && (
                    <p className="text-sm text-slate-500 mt-2">
                      {services.find(s => s.service_id === selectedService)?.description}
                    </p>
                  )}
                  {services.find(s => s.service_id === selectedService)?.creates_rental && (
                    <div className="mt-4 p-3 rounded-sm bg-amber-50 border border-amber-200 space-y-2">
                      <Label htmlFor="expected-return-date" className="text-sm font-medium text-amber-900">
                        Expected Return Date
                      </Label>
                      <input
                        id="expected-return-date"
                        type="date"
                        data-testid="expected-return-date-input"
                        value={expectedReturnDate}
                        onChange={(e) => setExpectedReturnDate(e.target.value)}
                        min={selectedDate || new Date().toISOString().split("T")[0]}
                        className="w-full rounded-sm border border-amber-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      />
                      <p className="text-xs text-amber-800">
                        This service will reserve a rental item. Leave blank to use the default rental duration.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Time Slots */}
            {selectedDate && (
              <Card className="rounded-sm border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">{services.length > 0 ? '4' : '3'}. Select Time Slot</CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="space-y-2">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.start_time}
                          onClick={() =>
                            slot.available &&
                            setSelectedSlot({
                              start_time: slot.start_time,
                              end_time: slot.end_time,
                            })
                          }
                          disabled={!slot.available}
                          className={`w-full p-3 rounded-sm border text-left transition-colors ${
                            selectedSlot?.start_time === slot.start_time
                              ? "border-indigo-500 bg-indigo-50"
                              : slot.available
                              ? "border-slate-200 hover:border-indigo-300 hover:bg-slate-50"
                              : "border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed"
                          }`}
                          data-testid={`slot-${slot.start_time}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-slate-400" />
                              <span className="font-medium">
                                {slot.start_time} - {slot.end_time}
                              </span>
                            </div>
                            {!slot.available && (
                              <Badge className="rounded-sm bg-slate-200 text-slate-500">
                                Booked
                              </Badge>
                            )}
                            {selectedSlot?.start_time === slot.start_time && (
                              <CheckCircle2 className="h-5 w-5 text-indigo-600" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-4">
                      No time slots available for this date
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Book Button */}
            {selectedSlot && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="w-full rounded-sm bg-slate-900 hover:bg-slate-800"
                size="lg"
                data-testid="confirm-booking-btn"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Confirm Booking
              </Button>
            )}
          </div>

          {/* Right Column - Calendar */}
          <Card className="rounded-sm border-slate-200 lg:col-span-2">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{services.length > 0 ? '3' : '2'}. Select Date</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigateMonth(-1)}
                    className="rounded-sm h-8 w-8"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium min-w-[140px] text-center">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigateMonth(1)}
                    className="rounded-sm h-8 w-8"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div
                    key={day}
                    className="text-center text-sm font-medium text-slate-500 py-2"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth().map((day, index) => {
                  const dayBookings = getBookingsForDay(day);
                  const isToday =
                    day &&
                    new Date().toDateString() ===
                      new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth(),
                        day
                      ).toDateString();
                  const isPast = isPastDay(day);
                  const isSelected = isSelectedDay(day);

                  return (
                    <button
                      key={`${currentDate.getFullYear()}-${currentDate.getMonth()}-${day ?? `pad-${index}`}`}
                      onClick={() => handleDateSelect(day)}
                      disabled={!day || isPast}
                      className={`min-h-[80px] p-2 border rounded-sm text-left transition-colors ${
                        !day
                          ? "bg-slate-50 cursor-default"
                          : isPast
                          ? "bg-slate-100 cursor-not-allowed opacity-50"
                          : isSelected
                          ? "border-indigo-500 bg-indigo-50 border-2"
                          : "bg-white hover:bg-slate-50 cursor-pointer"
                      } ${isToday && !isSelected ? "border-indigo-300" : "border-slate-200"}`}
                      data-testid={day ? `day-${day}` : undefined}
                    >
                      {day && (
                        <>
                          <div
                            className={`text-sm font-medium mb-1 ${
                              isToday
                                ? "text-indigo-600"
                                : isPast
                                ? "text-slate-400"
                                : "text-slate-700"
                            }`}
                          >
                            {day}
                            {isToday && (
                              <span className="ml-1 text-xs text-indigo-500">Today</span>
                            )}
                          </div>
                          {dayBookings.length > 0 && (
                            <div className="space-y-1">
                              {dayBookings.map((booking) => (
                                <div
                                  key={booking.booking_id}
                                  className={`text-xs p-1 rounded truncate ${
                                    booking.status === "confirmed"
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-amber-100 text-amber-700"
                                  }`}
                                >
                                  {booking.time_slot.start_time}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-300"></div>
                  <span className="text-sm text-slate-600">Confirmed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-amber-100 border border-amber-300"></div>
                  <span className="text-sm text-slate-600">Pending</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm border-2 border-indigo-500 bg-indigo-50"></div>
                  <span className="text-sm text-slate-600">Selected</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Service Booking</DialogTitle>
            <DialogDescription>
              Please review your booking details before confirming.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-sm space-y-3">
              <div className="flex items-center gap-2">
                <Cog className="h-4 w-4 text-slate-400" />
                <span className="font-medium">
                  {selectedInstallationData?.system_name}
                  {selectedInstallationData?.location_name
                    ? ` - ${selectedInstallationData.location_name}`
                    : ""}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span>{selectedDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-400" />
                <span>
                  {selectedSlot?.start_time} - {selectedSlot?.end_time}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special requests or information..."
                rows={3}
                className="rounded-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
              data-testid="submit-booking-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Confirm Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookService;
